// packages/api-client/src/services/modelService.ts
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  AgentResponse, // Updating an agent's model still returns AgentResponse
  AvailableLLM,
  AvailableModelsApiResponse,
  AvailableModelsResponse, // This is AvailableLLM[]
  ServiceResponse,
  UUID,
} from '@dopomogai/types';

export const modelService = {
  /**
   * Updates the LLM model for a specific agent.
   * This calls the agent update endpoint.
   */
  updateModel: (agentId: UUID, llmModel: string): ServiceResponse<AgentResponse> => {
    const payload = { llm_model: llmModel };
    return getApiClient().put<AgentResponse>(`/agents/${agentId}`, payload)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating model for agent ${agentId}`));
  },

  /**
   * Fetches the list of available LLM models from the backend.
   * Backend returns { models: [{ id, label, provider, type }], default: string }.
   * We map label -> name for the AvailableLLM type.
   */
  getAvailableModels: (): ServiceResponse<AvailableModelsResponse> => {
    return getApiClient().get<AvailableModelsApiResponse | AvailableModelsResponse>('/llm/available')
      .then(response => {
        const payload: any = response.data;

        // Preferred: payload is an object with a "models" array and a "default" descriptor.
        if (payload && typeof payload === 'object' && !Array.isArray(payload) && Array.isArray(payload.models)) {
          const mapped: AvailableLLM[] = (payload.models as any[]).map((m: any) => ({
            id: m.id,
            name: m.label,                // Map backend "label" -> FE "name"
            provider: m.provider,
            description: null,
            // Optionally retain type information or other fields:
            // You can extend AvailableLLM if needed to include "type" or other metadata.
          }));
          return mapped;
        }

        // Backwards-compat: if payload is already an array (legacy shape), return it as-is
        if (Array.isArray(payload)) {
          return payload as AvailableModelsResponse;
        }

        // Fallback to empty list
        return [];
      })
      .catch(err => handleApiError(err, 'fetching available LLM models'));
  },
};